import React from 'react';
import type { SearchHistoryItem } from '../../../utils/searchHistory';

// Place type filter options
export enum PlaceType {
  ADDRESS = 'address',
  BUILDING = 'building',
  STREET = 'street',
  CITY = 'city',
  POI = 'poi',
  NATURAL = 'natural',
}

// Nominatim feature types mapping
export interface NominatimFilter {
  featureType?: string;
  layer?: string;
}

// Search settings interface
export interface SearchSettings {
  selectedPlaceTypes: Set<PlaceType>;
  useLocationPriority: boolean;
}

export interface AddressResult {
  id: string;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  importance: number;
  distance?: number; // Distance from user location in km
}

export interface AddressSearchBarProps {
  onSelectLocation?: (lat: number, lon: number, address: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Enable collapsible mode - shows only a button when collapsed (mobile) */
  collapsible?: boolean;
  /** Start in collapsed state when collapsible=true (default: true) */
  defaultCollapsed?: boolean;
}

export interface SearchInputProps {
  query: string;
  onQueryChange: (query: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onFocus: () => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onClear: () => void;
  isLoading: boolean;
  disabled: boolean;
  placeholder: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export interface SearchResultsProps {
  results: AddressResult[];
  selectedIndex: number;
  onSelectResult: (result: AddressResult) => void;
  showRecentSearches: boolean;
  recentSearches: SearchHistoryItem[];
  onSelectRecentSearch: (recentSearch: SearchHistoryItem) => void;
  error?: string | null;
}

export interface SearchResultItemProps {
  result: AddressResult;
  index: number;
  isSelected: boolean;
  onSelect: (result: AddressResult) => void;
  isRecent?: boolean;
}

export interface RecentSearchItemProps {
  recentSearch: SearchHistoryItem;
  index: number;
  isSelected: boolean;
  onSelect: (recentSearch: SearchHistoryItem) => void;
}

export interface PlaceTypeBadgeProps {
  type: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export interface PlaceTypeFilterProps {
  selectedTypes: Set<PlaceType>;
  onTypesChange: (types: Set<PlaceType>) => void;
  useLocationPriority: boolean;
  onLocationPriorityChange: (enabled: boolean) => void;
  hasUserLocation: boolean;
  className?: string;
}

// Re-export for convenience
export type { SearchHistoryItem };
